// BeneAIToolResultCard — BeneAI-02.
//
// Renders a read-tool result so the user sees what BeneAI found.
// Tries to surface a friendly preview from the output payload
// (e.g. ``rows`` for list tools, ``account``/``commitment`` keys
// for get tools); falls back to a compact JSON dump so the
// information is still legible even before we ship per-tool result
// renderers.

import { AlertTriangle, Check } from "lucide-react";
import { useMemo } from "react";

import type { BeneAIToolResultBlock } from "../types";


export function BeneAIToolResultCard({
  block,
}: { block: BeneAIToolResultBlock }) {
  const preview = useMemo(() => summarizeOutput(block), [block]);
  if (block.outcome !== "success") {
    return (
      <div
        data-testid="beneai-tool-result"
        data-outcome={block.outcome}
        className="self-start max-w-[85%] rounded-md border border-ember-tint bg-canvas px-3 py-2 text-xs"
      >
        <div className="flex items-center gap-1.5 text-ember-ink">
          <AlertTriangle size={12} aria-hidden="true" />
          <span className="font-medium">
            {humanize(block.tool_name)} {labelForOutcome(block.outcome)}
          </span>
        </div>
        {block.error_message && (
          <div className="mt-1 text-text-secondary">
            {block.error_message}
          </div>
        )}
      </div>
    );
  }
  return (
    <div
      data-testid="beneai-tool-result"
      data-outcome="success"
      className="self-start max-w-[85%] rounded-md border border-sage-tint bg-canvas px-3 py-2 text-xs"
    >
      <div className="flex items-center gap-1.5 text-teal">
        <Check size={12} aria-hidden="true" />
        <span className="font-medium text-text-primary">
          {humanize(block.tool_name)}
        </span>
        <span className="text-text-tertiary">result</span>
      </div>
      {preview && (
        <div className="mt-1 whitespace-pre-wrap break-words text-text-secondary">
          {preview}
        </div>
      )}
    </div>
  );
}


function humanize(toolName: string): string {
  return toolName.replace(/_/g, " ");
}


function labelForOutcome(o: BeneAIToolResultBlock["outcome"]): string {
  if (o === "rejected") return "wasn't allowed";
  return "failed";
}


// Best-effort preview that surfaces the most common shapes returned
// by the v2 tool catalog. Anything we don't recognize falls back to
// a compact pretty-printed JSON snippet so the user still sees
// data.
function summarizeOutput(block: BeneAIToolResultBlock): string | null {
  const o = block.output_json;
  if (!o || typeof o !== "object") return null;
  const obj = o as Record<string, unknown>;
  if (Array.isArray(obj.rows)) {
    const n = obj.rows.length;
    const total =
      typeof obj.total === "number" ? obj.total : n;
    if (n === 0) return "No matching records.";
    return `${n} of ${total} record${total === 1 ? "" : "s"}.`;
  }
  for (const key of [
    "account", "commitment", "playbook", "touchpoint",
    "plan_year", "team_member", "account_contact",
  ]) {
    const v = obj[key];
    if (v && typeof v === "object") {
      const name =
        (v as Record<string, unknown>).legal_name
        ?? (v as Record<string, unknown>).title
        ?? (v as Record<string, unknown>).display_name
        ?? (v as Record<string, unknown>).name;
      if (typeof name === "string") return name;
    }
  }
  try {
    const json = JSON.stringify(obj);
    if (json.length > 220) return `${json.slice(0, 220)}…`;
    return json;
  } catch {
    return null;
  }
}
